import { Button, ButtonVariants, getApiUrl, PhotoMetaSummary, rawPost } from "@app/shared";
import * as PhotoMetaApi from "@app/shared/src/api/PhotoMetaControllerClient.gen";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { toastError } from "../../../../helpers";
import { useConfirmDialog } from "../../../../hooks";
import { PhotoBulkEditModal } from "./PhotoBulkEditModal";
import { PhotoDetailModal } from "./PhotoDetailModal";
import { PhotoFilters, PhotoFilterValues } from "./PhotoFilters";

const PAGE_SIZE = 60;
const FILTER_DEBOUNCE_MS = 300;

export const PhotoLibrary: FC = () => {
    const [photos, setPhotos] = useState<PhotoMetaSummary[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [nextCursor, setNextCursor] = useState<string | undefined>();
    const [totalCount, setTotalCount] = useState(0);
    const [filters, setFilters] = useState<PhotoFilterValues>({});
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [detailPhoto, setDetailPhoto] = useState<PhotoMetaSummary | null>(null);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    const [showFilters, setShowFilters] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    // Monotonic request counter — responses older than this are ignored.
    const latestRequestId = useRef(0);
    const loadMoreInFlight = useRef(false);
    const confirmDialog = useConfirmDialog();

    const runSearch = useCallback(
        async (currentFilters: PhotoFilterValues, reset: boolean, cursor?: string) => {
            const requestId = ++latestRequestId.current;
            setIsLoading(true);
            try {
                const result = await PhotoMetaApi.search(
                    PAGE_SIZE,
                    "targetCaptureDate",
                    "desc",
                    currentFilters.organizationId,
                    currentFilters.deviceId,
                    currentFilters.weather,
                    currentFilters.timeOfDay,
                    currentFilters.scene,
                    currentFilters.targetTypesInView,
                    currentFilters.fhwaClassesInView,
                    currentFilters.dewarpedImage,
                    currentFilters.needsReview,
                    currentFilters.numLanes,
                    currentFilters.dateFrom,
                    currentFilters.dateTo,
                    currentFilters.minWidth,
                    currentFilters.maxWidth,
                    currentFilters.minHeight,
                    currentFilters.maxHeight,
                    currentFilters.addedToTrainingDataset,
                    currentFilters.trainingDatasetName,
                    cursor
                );

                // Ignore stale responses: a newer request has superseded this one.
                if (requestId !== latestRequestId.current) return;

                if (reset) {
                    setPhotos(result?.data ?? []);
                } else {
                    setPhotos((prev) => {
                        const existingIds = new Set(prev.map((p) => p.id));
                        const newPhotos = (result?.data ?? []).filter((p) => !existingIds.has(p.id));
                        return [...prev, ...newPhotos];
                    });
                }
                setHasMore(result?.hasMore ?? false);
                setNextCursor(result?.nextCursor);
                setTotalCount((prev) => (reset ? result?.data?.length ?? 0 : prev + (result?.data?.length ?? 0)));
            } catch (err) {
                if (requestId === latestRequestId.current) {
                    console.error("Failed to fetch photos:", err);
                }
            } finally {
                if (requestId === latestRequestId.current) {
                    setIsLoading(false);
                }
            }
        },
        []
    );

    // Debounced filter-driven fetch. Resets pagination state and kicks off a new search.
    // Any in-flight request is invalidated via latestRequestId so late responses don't
    // overwrite the results for the current filter selection.
    useEffect(() => {
        const timer = setTimeout(() => {
            setSelectedIds(new Set());
            setNextCursor(undefined);
            setHasMore(true);
            setTotalCount(0);
            // Scroll back to the top so the user sees the new results from the start and the
            // scroll-driven loader doesn't immediately fire a cascade of page loads while the
            // list shrinks from under a scrolled-down position.
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
            runSearch(filters, true);
        }, FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filters, runSearch]);

    const handleLoadMore = useCallback(async () => {
        if (!hasMore || !nextCursor || loadMoreInFlight.current) return;
        loadMoreInFlight.current = true;
        try {
            await runSearch(filters, false, nextCursor);
        } finally {
            loadMoreInFlight.current = false;
        }
    }, [hasMore, nextCursor, filters, runSearch]);

    // Load more via an IntersectionObserver watching a sentinel at the bottom of the list.
    // This is more reliable than a scroll-position threshold: it fires regardless of which
    // element actually scrolls and, because the effect re-subscribes whenever handleLoadMore
    // changes (i.e. after each page updates nextCursor), it keeps pulling pages until the
    // sentinel is pushed out of view — so a short result set that doesn't fill the viewport
    // still loads everything instead of stranding at the first page.
    useEffect(() => {
        const root = scrollRef.current;
        const sentinel = sentinelRef.current;
        if (!root || !sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) handleLoadMore();
            },
            { root, rootMargin: "600px" }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [handleLoadMore]);

    const toggleSelect = (id: string | undefined) => {
        if (!id) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAll = () => {
        setSelectedIds(new Set(photos.map((p) => p.id!).filter(Boolean)));
    };

    const deselectAll = () => setSelectedIds(new Set());

    const handleDelete = () => {
        confirmDialog.show(
            "Delete Photos?",
            `Delete ${selectedIds.size} photo(s)? This cannot be undone.`,
            "Delete",
            async () => {
                await PhotoMetaApi.bulkDelete(Array.from(selectedIds));
                setPhotos((prev) => prev.filter((p) => !selectedIds.has(p.id!)));
                setSelectedIds(new Set());
            }
        );
    };

    const handleDownload = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0 || isDownloading) return;

        setIsDownloading(true);
        try {
            const response = await rawPost("/api/photometa/download-zip", JSON.stringify(ids), {
                headers: { "Content-Type": "application/json" },
            });
            if (!response.ok) {
                console.error("Zip download failed:", response.status, response.statusText);
                toastError("Download failed", `Could not download zip (${response.status} ${response.statusText}).`);
                return;
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `omnisight-photos-${new Date().toISOString().replace(/[:.]/g, "-")}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Zip download error:", err);
            toastError("Download failed", "Could not download zip. Check the console for details.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleBulkEditDone = () => {
        setShowBulkEdit(false);
        setSelectedIds(new Set());
        runSearch(filters, true);
    };

    const handlePhotoSaved = (updated: PhotoMetaSummary) => {
        setPhotos((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        setDetailPhoto(updated);
    };

    const detailIndex = detailPhoto ? photos.findIndex((p) => p.id === detailPhoto.id) : -1;

    const handlePrev = detailIndex > 0 ? () => setDetailPhoto(photos[detailIndex - 1]) : undefined;
    const handleNext = detailIndex >= 0 && detailIndex < photos.length - 1 ? () => setDetailPhoto(photos[detailIndex + 1]) : undefined;

    return (
        <div className="flex h-full" style={{ height: "calc(100vh - 140px)" }}>
            {showFilters && (
                <div className="w-72 flex-shrink-0 overflow-y-auto p-4 border-r border-gray-200 bg-gray-50">
                    <PhotoFilters filters={filters} onChange={setFilters} />
                </div>
            )}

            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
                    <div className="flex items-center gap-3">
                        <Button variant={ButtonVariants.Secondary} onClick={() => setShowFilters((v) => !v)}>
                            {showFilters ? "Hide Filters" : "Show Filters"}
                        </Button>
                        <span className="text-sm text-gray-500">
                            {photos.length} photos loaded {hasMore && "(scroll for more)"}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {photos.length > 0 && (
                            <Button variant={ButtonVariants.Secondary} onClick={selectedIds.size === photos.length ? deselectAll : selectAll}>
                                {selectedIds.size === photos.length ? "Deselect All" : "Select All"}
                            </Button>
                        )}
                    </div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
                    {photos.length === 0 && !isLoading && (
                        <div className="flex items-center justify-center h-full text-gray-400 text-sm">No photos found. Try adjusting filters.</div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                        {photos.map((photo) => (
                            <div
                                key={photo.id}
                                onClick={() => setDetailPhoto(photo)}
                                className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${selectedIds.has(photo.id!) ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200 hover:border-gray-300"
                                    }`}
                            >
                                <div className="aspect-[4/3] bg-gray-100">
                                    <img
                                        src={getApiUrl(`/api/photometa/${photo.id}/thumbnail`)}
                                        alt=""
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = "none";
                                        }}
                                    />
                                </div>

                                <div
                                    className="absolute top-1.5 left-1.5 z-10"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSelect(photo.id);
                                    }}
                                >
                                    <div
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selectedIds.has(photo.id!) ? "bg-blue-500 border-blue-500" : "bg-white/80 border-gray-400 group-hover:border-gray-500"
                                            }`}
                                    >
                                        {selectedIds.has(photo.id!) && (
                                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {photo.needsReview && (
                                    <div className="absolute top-1.5 right-1.5 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                                        REVIEW
                                    </div>
                                )}

                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
                                    <p className="text-[10px] text-white/90 truncate">{photo.deviceName ?? "Unknown device"}</p>
                                    <p className="text-[10px] text-white/70">{photo.targetCaptureDate?.toLocaleDateString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {isLoading && (
                        <div className="flex justify-center py-8">
                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {/* Sentinel: when this scrolls into view (or is already visible) the
                        IntersectionObserver above triggers loading the next page. */}
                    <div ref={sentinelRef} aria-hidden className="h-px" />
                </div>

                {selectedIds.size > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white shadow-lg">
                        <span className="text-sm font-medium text-gray-700">{selectedIds.size} selected</span>
                        <div className="flex items-center gap-2">
                            <Button onClick={() => setShowBulkEdit(true)}>Edit Metadata</Button>
                            <Button variant={ButtonVariants.Secondary} onClick={handleDownload} disabled={isDownloading}>{isDownloading ? "Downloading..." : "Download Zip"}</Button>
                            <Button variant={ButtonVariants.Destructive} onClick={handleDelete}>Delete</Button>
                            <Button variant={ButtonVariants.Secondary} onClick={deselectAll}>Cancel</Button>
                        </div>
                    </div>
                )}
            </div>

            <PhotoDetailModal
                show={detailPhoto !== null}
                photo={detailPhoto}
                onClose={() => setDetailPhoto(null)}
                onSaved={handlePhotoSaved}
                onPrev={handlePrev}
                onNext={handleNext}
            />

            <PhotoBulkEditModal
                show={showBulkEdit}
                selectedIds={Array.from(selectedIds)}
                onClose={() => setShowBulkEdit(false)}
                onUpdated={handleBulkEditDone}
            />

            {confirmDialog.renderDialog()}
        </div>
    );
};
